import React, {useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from "react-hook-form";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import classNames from "classnames";
import { useNavigate, useParams } from "react-router-dom"; // Импортируем useNavigate

import MainMeta from '../../metaTags/MainMeta';
import ButtonLink from '../../components/Button';

import "./index.scss";

const FormField = ({ label, error, children }) => (
  <div className="form-field">
    {label && <label className="form-label">{label}</label>} {/* Лейбл сверху */}
    {children} {/* Поле ввода */}
    {error && <p className="form-error">{error.message}</p>} {/* Ошибка снизу */}
  </div>
);

function Reservation(){
  const { lang } = useParams(); // Получаем текущий язык из URL
  const { t } = useTranslation();
  const { handleSubmit, reset, formState: { errors }, control } = useForm();
  const { executeRecaptcha } = useGoogleReCaptcha(); // Для работы с reCAPTCHA
  const [serverResponse, setServerResponse] = useState('');
  const navigate = useNavigate(); // Используем useNavigate для перехода
  const [status, setStatus] = useState(""); // Для отображения статуса отправки
  const [timeMin, setTimeMin] = useState(null); // Минимальное время
  const [timeMax, setTimeMax] = useState(null); // Максимальное время
  const phoneInputRef = useRef(null);

  const cleanInpts = () => {
    setTimeout(() => {
      const dialCode = phoneInputRef.current.state.selectedCountry.dialCode
      setStatus("");
      reset({
        name: "",      
        phone: dialCode,
        date: null,
        time: null,
        invitees: 1,
        comment: "",
        policy: false,
      })
    }, 500);
  }


  const handleDateChange = (selectedDate) => {
    if (!selectedDate) return;

    const now = new Date(); 
    const isToday = selectedDate.toDateString() === now.toDateString(); 
    const dayOfWeek = selectedDate.getDay();

    if (dayOfWeek === 2 || dayOfWeek === 3 || dayOfWeek === 4 || dayOfWeek === 5) {
      // Если вторник, среда, четверг, пятница, задаём диапазон времени
      setTimeMin((isToday && new Date(now.setMinutes(now.getMinutes() + 15)) >= new Date().setHours(12, 0, 0, 0)) 
      ? new Date(now.setMinutes(now.getMinutes() + 15)) 
      : new Date().setHours(12, 0, 0, 0));
      setTimeMax(new Date().setHours(19, 30, 0, 0)); // 7:30 PM
    } if (dayOfWeek === 6) {
      // Если суббота 
      setTimeMin((isToday && new Date(now.setMinutes(now.getMinutes() + 15)) >= new Date().setHours(12, 0, 0, 0))
      ? new Date(now.setMinutes(now.getMinutes() + 15)) 
      : new Date().setHours(12, 0, 0, 0)); 
      setTimeMax(new Date().setHours(20, 30, 0, 0)); // 8:30 PM
    } if (dayOfWeek === 0) {
      // Если воскресенье 
      setTimeMin((isToday && new Date(now.setMinutes(now.getMinutes() + 15)) >= new Date().setHours(16, 0, 0, 0)) 
      ? new Date(now.setMinutes(now.getMinutes() + 15)) 
      : new Date().setHours(16, 0, 0, 0)); 
      setTimeMax(new Date().setHours(20, 30, 0, 0)); // 8:30 PM
    }
  };

  // Функция для преобразования даты в формат ДД/ММ/ГГГГ
  const formatDate = (date) => {
    if (!date) return null;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Месяцы начинаются с 0
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const formatTime = (time) => {
    if (!time) return null;
    let minutes = String(time.getMinutes());
    const hours = String(time.getHours()); 
    if(minutes === "0"){
      minutes = "00";
    }
    return `${hours}:${minutes}`;
  };

  const onSubmit = async (data) => {
    if (!executeRecaptcha) {
      setStatus("reCAPTCHA недоступна.");
      return;
    }

    
    try {
      
      // Получение reCAPTCHA токена
      const token = await executeRecaptcha("form_submission");
      
      const formattedData = {
          ...data,
          date: formatDate(data.date),
          time: formatTime(data.time),
        };

      const response = await fetch("https://borscht.nl/send-email", {//http://localhost:3001/send-email    https://borscht-2024.dc3.lemon-solutions.net/send-email
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...formattedData, token, lang }),
      });

      const responseText = await response.text();

      if (response.ok) {
        setStatus("Email отправлен успешно!");
        cleanInpts();
        setTimeout(() => {
          navigate(`/${lang}/statusSuccess`);
        }, 1000);
      } else {
        setStatus(`Ошибка при отправке email.`);
      }

      cleanInpts()

    } catch (error) {
      setStatus(`Ошибка при подключении к серверy ${error.message}/`);
    }
  };

  return(
      <div className="reservation-wrapper">
        <MainMeta />
        <div className='title'>
            <div className='text big'>{t('reservation')}</div>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='inputs-block'>
          <FormField label={t('name')} error={errors.name}>
            <Controller
              name="name"
              control={control}
              defaultValue=""
              rules={{
                required: `${t('error-name')}`,
                minLength: { value: 3, message: "Имя должно быть не менее 3 символов." },
                pattern: { value: /^[A-Za-zА-Яа-яІіЁё0-9\s]+$/i, message: "Имя содержит недопустимые символы." },
              }}
              render={({ field }) => (
                <input 
                  {...field} 
                  className={classNames("input-field name", {
                    "input-error": errors.name,
                  })}
                />
              )}
            />
          </FormField>
          
          <FormField label={t('phone')} error={errors.phone}>
            <Controller
              name="phone"
              control={control}
              rules={{
                required: `${t('error-phone')}`,
                validate: (value) => {
                  if (!value || value.length < 10) {
                    return "Номер телефона должен быть корректным.";
                  }
                  return true;
                },
              }}
              render={({ field: { onChange, value } }) => (
                <PhoneInput
                  ref={phoneInputRef} 
                  value={value}
                  onChange={onChange}
                  country={"nl"} 
                  countryCodeEditable={false} // Запретить редактирование кода страны
                  buttonStyle={{
                    backgroundColor: "rgb(247, 244, 238)", 
                    borderRadius: "0", 
                    border: errors.phone ? "1px solid red" : "1px solid rgba(219, 210, 188)",
                  }}
                  inputStyle={{
                    width: "100%",
                    height: "3rem",
                    fontSize: "1.2rem",
                    border: errors.phone ? "1px solid red" : "1px solid rgba(219, 210, 188)", 
                  }}
                  />
                )}
            />
          </FormField>

          <FormField label={t('e-mail')} error={errors.mail}>
            <Controller
              name="mail"
              control={control}
              defaultValue=""
              rules={{
                pattern: { value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, message: `${t('error-e-mail')}` },
              }}
              render={({ field }) => (
                <input 
                  {...field} 
                  className={classNames("input-field name", {
                    "input-error": errors.mail,
                  })}
                />
              )}
            />
          </FormField>

          <div className='date-block'>
            <FormField label={t('date')} error={errors.date}>
              <Controller
                name="date"
                control={control}
                defaultValue={null}
                rules={{ required: `${t('error-date')}` }} 
                render={({ field }) => (
                  <DatePicker
                  minDate={new Date()}
                  selected={field.value}
                  onChange={(date) => {
                    field.onChange(date);
                    handleDateChange(date); // Обновляем диапазон времени
                  }}
                  dateFormat="dd-MM-yyyy"
                  // filterDate={(date) => date.getDay() !== 1} 
                  filterDate={(date) => {
                    const day = date.getDay();
                    const isClosedPeriod = date >= new Date(2025, 1, 15) && date <= new Date(2025, 1, 21); // 15.02.2025 - 21.02.2025
                    return day !== 1 && !isClosedPeriod; // Исключаем понедельники и заданный период
                  }}
                  className={classNames("input-field date", {
                    "input-error": errors.date,
                  })}
                  />
                )}
                />
            </FormField>

            <FormField label={t('time')} error={errors.time}>
              <Controller
                name="time"
                control={control}
                defaultValue={null}
                rules={{ required: `${t('error-time')}` ,
                  validate: (selectedTime) => {
                    if (!selectedTime) return `${t('error-time')}`;
                    return true; 
                  },
                }} 
                render={({ field }) => (
                  <DatePicker
                  selected={field.value}
                  onChange={(time) => field.onChange(time)}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption={t('time')}
                  dateFormat="HH:mm"
                  minTime={timeMin ? new Date(timeMin) : undefined} 
                  maxTime={timeMax ? new Date(timeMax) : undefined} 
                  className={classNames("input-field time", {
                    "input-error": errors.time,
                  })}
                  disabled={!timeMin || !timeMax} // Поле отключено, если диапазон времени не установлен
                  />
                )}
                />
            </FormField>

            <FormField label={t('guests')} error={errors.invitees}>
              <Controller
                name="invitees"
                control={control}
                defaultValue={1}
                rules={{
                  required: `${t('error-guests')}`,
                  min: { value: 1, message: `${t('error-guests')}` },
                }}
                render={({ field }) => (
                  <input 
                  {...field} 
                  type="number" 
                  min={1}
                  className={classNames("input-field invitees", {
                      "input-error": errors.invitees,
                    })}
                    />
                  )}
                  />
            </FormField>
          </div>

          <FormField label={t('additional-requirements')} error={errors.comment}>
            <Controller
              name="comment"
              control={control}
              defaultValue=""
              rules={{
                maxLength: { value: 500 },
              }}
              render={({ field }) => (
                <textarea 
                  {...field} 
                  className={classNames("input-field comment", {
                    "input-error": errors.comment,
                  })}
                  maxLength="500" 
                />
              )}
            />
          </FormField>

          <p style={{textTransform:"uppercase", margin: "2.5rem 0 2rem 0"}}>
          {t('reservation-info')}
          </p>
          <p>
          {t('reservation-info-1')}
          </p>
          <p>
          {t('reservation-info-2')}
          </p>
          <p>
          {t('reservation-info-3')}
          </p>

          <FormField error={errors.policy}>
            <Controller
              name="policy"
              control={control}
              defaultValue={false} 
              rules={{
                validate: (value) => value || `${t('error-agreement')}`,
              }}
              render={({ field: { onChange, value } }) => (
                <div className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    id="policy"
                    checked={value} 
                    onChange={(e) => onChange(e.target.checked)} 
                    className={classNames("checkbox-field", {
                      "input-error": errors.policy,
                      "checkbox-field:checked": value,
                    })}
                  />
                  <label htmlFor="policy" className="checkbox-label">
                  {t('agreement')}
                  </label>
                </div>
              )}
            />
          </FormField>
        </div>

          {status && <p className="form-status">{status}</p>}
          <button className="btn-link-wrapper btn-menu btn-with-borders" style={{border:"none", letterSpacing:"3px"}} type="submit">
              <span>{t('reservation')}</span>
          </button>
        </form>
      </div>
  )
}

export default Reservation; 




